import { type Card, DECK } from './cards.js';
import { nextInt, shuffle } from './rng.js';
import { type Seat, nextSeat } from './trick.js';

export const HAND_SIZE = 8;
/** Belgian tables deal in 3-2-3 packets. Cosmetic, but it is how it is done. */
export const PACKETS = [3, 2, 3] as const;
/**
 * Nobody at a real table shuffles a gathered pack more than a few times, and
 * that is exactly why manille hands run in suits: three riffles do not come
 * close to randomising 32 cards, so the tricks you just picked up still leave
 * their shape in the next deal. Emulating the sloppy shuffle is the point.
 */
export const SHUFFLES = 3;

/**
 * One riffle: cut near the middle, then drop cards from whichever half still
 * has the most left (Gilbert–Shannon–Reeds), which is what a real riffle does.
 */
function riffle<T>(items: readonly T[], seed: number): readonly [T[], number] {
  let state = seed;
  // A hand-cut lands within a few cards of the middle, never dead centre.
  const [jitter, afterJitter] = nextInt(state, Math.max(1, Math.floor(items.length / 5)));
  state = afterJitter;
  const cut = Math.floor(items.length / 2) + jitter - Math.floor(items.length / 10);
  const left = items.slice(0, Math.max(1, Math.min(items.length - 1, cut)));
  const right = items.slice(left.length);
  const out: T[] = [];
  let i = 0;
  let j = 0;
  while (i < left.length || j < right.length) {
    const remaining = left.length - i + (right.length - j);
    const [pick, afterPick] = nextInt(state, remaining);
    state = afterPick;
    // Thumbs release cards in little clumps of one to three, not one at a time.
    // That is precisely why a hand shuffled three times still has runs in it.
    const [clump, afterClump] = nextInt(state, 3);
    state = afterClump;
    const fromLeft = i < left.length && pick < left.length - i;
    for (let n = 0; n <= clump; n += 1) {
      if (fromLeft) {
        if (i >= left.length) break;
        out.push(left[i] as T);
        i += 1;
      } else {
        if (j >= right.length) break;
        out.push(right[j] as T);
        j += 1;
      }
    }
  }
  return [out, state];
}

export interface DealtCards {
  /** Indexed by seat. */
  readonly hands: readonly Card[][];
  /** The player to the dealer's left leads the first trick. */
  readonly leader: Seat;
  readonly seed: number;
}

/**
 * Eight cards each, clockwise from the dealer's left, in 3-2-3 packets. There
 * is no stock and no turned-up card: in Belgian manillen the dealer looks at
 * their hand and *names* trump (see `game.ts`, the `declaring` phase).
 *
 * `gathered` is the previous deal's tricks, stacked as they were picked up. Given
 * one, the pack is riffled `SHUFFLES` times like a real table would and dealt
 * from; without one — the first deal of a match, out of the box — it is properly
 * randomised instead.
 */
export function dealCards(dealer: Seat, seed: number, gathered?: readonly Card[]): DealtCards {
  const usable = gathered !== undefined && new Set(gathered).size === DECK.length;
  let deck: Card[];
  let nextSeedValue: number;
  if (usable) {
    deck = [...(gathered as readonly Card[])];
    nextSeedValue = seed;
    for (let i = 0; i < SHUFFLES; i += 1) {
      const [riffled, next] = riffle(deck, nextSeedValue);
      deck = riffled;
      nextSeedValue = next;
    }
  } else {
    [deck, nextSeedValue] = shuffle(DECK, seed) as [Card[], number];
  }
  const hands: Card[][] = [[], [], [], []];
  let index = 0;
  for (const packet of PACKETS) {
    let seat = nextSeat(dealer);
    for (let i = 0; i < 4; i += 1) {
      for (let n = 0; n < packet; n += 1) {
        (hands[seat] as Card[]).push(deck[index] as Card);
        index += 1;
      }
      seat = nextSeat(seat);
    }
  }
  return { hands, leader: nextSeat(dealer), seed: nextSeedValue };
}
